import type { AccessDelegationWithType } from "@/modules/access/delegation/accessDelegationApi.ts";
import type { AccessImpersonationWithType } from "@/modules/access/impersonation/accessImpersonationApi.ts";
import type { ApiTokenWithType } from "@/modules/access/token/apiTokenApi.ts";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import type { DiskWithType } from "@/modules/disks/disksListQuery.ts";
import type { MetalListItemWithType } from "@/modules/metal/metalQuery.ts";
import type { FirewallWithType } from "@/modules/network/firewall/firewallApi.ts";
import type { IpAddressWithType } from "@/modules/network/ipAddress/ipAddressApi.ts";
import type { LoadBalancerWithType } from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import type { VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import type { ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import type { StorageAccountWithPrice } from "@/modules/storage/account/storageAccountApi.ts";
import type { BucketWithType } from "@/modules/storage/bucket/bucketApi";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";

export type AnyResource =
    | ApiTokenWithType
    | AccessDelegationWithType
    | IpAddressWithType
    | LoadBalancerWithType
    | VpcWithType
    | VirtualMachineLoc
    | ManagedServiceLoc
    | BucketWithType
    | StorageAccountWithPrice
    | AccessImpersonationWithType
    | MetalListItemWithType
    | FirewallWithType
    | DiskWithType;

export function getResourceName(item: AnyResource): string {
    const { $type } = item;
    switch ($type) {
        case "api_token":
            return item.description;

        case "access_delegation":
            return item.name;

        case "ip_address":
            return item.name ? item.name : item.address;

        case "load_balancer":
            return item.display_name || item.private_address;

        case "virtual_machine":
            return item.name;

        case "storage_account":
            return item.title;

        case "bucket":
            return item.name;

        case "vpc":
            return item.name ?? "";

        case "managed_service":
            return item.display_name;

        case "access_impersonation":
            return item.access_owner;

        case "bare_metal":
            return item.display_name;

        case "firewall":
        case "disk":
            return item.display_name ?? "";

        default:
            exhaustiveSwitchCheck($type);
    }
}
